import { ALWAYS_ON_SECTION, HR_SECTION_KEYS, parseSectionSetting, type HrSectionKey } from '@vigeo/shared';

/**
 * Which sections of the HR app are switched on in this build (#610).
 *
 * One build setting, `VITE_ENABLED_SECTIONS`: a list of section names separated by commas or spaces.
 * Production is built with `roster`; local work, tests and staging do not give it at all.
 *
 *   not given             → every section is on, as before the setting existed
 *   given, names sections → those sections, and the Roster
 *   given, but empty or
 *   naming nothing known  → the Roster only
 *
 * So a typing mistake can only switch sections off. The Roster is always on: it is where the start
 * page and the "not available yet" page send people.
 *
 * THIS FILE IS NOT A SECURITY BOUNDARY. It keeps staff from being confused by screens that are not
 * ready. The lock is the server's (#625, `apps/api/src/auth/sections.ts`): a server PACKAGED with
 * the same list refuses the other sections' routes whatever a browser shows. A web build made
 * without the setting shows everything: the web app cannot tell which environment it is running in.
 */
export const SECTION_KEYS = HR_SECTION_KEYS;
export type SectionKey = HrSectionKey;

/** The section that can never be switched off. */
export const ALWAYS_ON: SectionKey = ALWAYS_ON_SECTION;
export const ROSTER_PATH = '/app/roster';

/**
 * The line under the app name when the Roster is the only section on (#724). The default, "Compliance &
 * Onboarding", names the sections a Roster-only build does not offer.
 */
export const ROSTER_ONLY_CAPTION = 'Staff roster';

/** The caption for the shell: the Roster-only one when nothing else is on, otherwise undefined (the shell's own). */
export function shellCaption(enabled: ReadonlySet<SectionKey> = enabledSections()): string | undefined {
  return SECTION_KEYS.every((key) => key === ALWAYS_ON || !enabled.has(key)) ? ROSTER_ONLY_CAPTION : undefined;
}

/** The names and the rule are shared with the server (#625): `packages/shared/src/sections.ts`. */
export function parseEnabledSections(setting: string | undefined): ReadonlySet<SectionKey> {
  return parseSectionSetting(setting, SECTION_KEYS, ALWAYS_ON);
}

/** Read on every call, not once at load, so a test can change the setting between renders. */
export function enabledSections(): ReadonlySet<SectionKey> {
  return parseEnabledSections(import.meta.env.VITE_ENABLED_SECTIONS);
}

export const sectionOn = (section: SectionKey): boolean => enabledSections().has(section);

const under = (pathname: string, root: string): boolean => pathname === root || pathname.startsWith(`${root}/`);

/**
 * The section a page under `/app` belongs to. `null` is an address this list does not know, and that
 * includes the style guide (`/app/design`): it is a developer's page, not a section, so it is shown
 * only while every section is on (#724) and a Roster-only build, production, does not serve it.
 */
export function sectionOfPath(pathname: string): SectionKey | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/app') return 'dashboard';
  if (under(path, ROSTER_PATH)) return 'roster';
  if (under(path, '/app/people') || under(path, '/app/offers') || under(path, '/app/interviews') || under(path, '/app/portal-links')) return 'hiring';
  if (under(path, '/app/review')) return 'review';
  if (under(path, '/app/forms')) return 'forms';
  return null;
}

/**
 * Whether the page at this address may be shown. An address the list does not know is shown only
 * while every section is on: a page added later stays hidden wherever anything is switched off,
 * until someone gives it a section here.
 */
export function pathAvailable(pathname: string, enabled: ReadonlySet<SectionKey> = enabledSections()): boolean {
  const section = sectionOfPath(pathname);
  if (section === null) return SECTION_KEYS.every((key) => enabled.has(key));
  return enabled.has(section);
}

/**
 * Whether the Roster draws its compliance and documents parts (#723): the five count boxes, the Compliance column and
 * filter, the quick look's compliance part, and the employee page's Compliance and Documents tabs and the cards that
 * summarise them.
 *
 * They are drawn only while EVERY section is on. Where anything is switched off, a person on the Roster was added by hand
 * and is never tracked (`isComplianceTracked`), and no record store holds a file, so each part would only read "Not tracked
 * yet" or "No files yet". Julio chose to hide them over a line that says so (#723, 2026-10-15). It is read from the same
 * section list as the menu and the pages, never from a flag of its own. Like the rest of this file it is not a security
 * boundary: the server's own section check is.
 */
export function complianceAndDocumentsShown(enabled: ReadonlySet<SectionKey> = enabledSections()): boolean {
  return SECTION_KEYS.every((key) => enabled.has(key));
}
